import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { it as itLocale, enGB, de as deLocale } from "date-fns/locale";
import { ArrowUp, CalendarIcon, Check, Loader2, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal } from "./Reveal";
import api, { formatApiError } from "../lib/api";
import { trackMeta, trackMetaCustom } from "../lib/metaPixel";
import { AERIAL_OPTIONS, DEFAULT_PRICING, PREMIUM_SURFACE, SIGNATURE_AERIAL_VIDEO_DELTA, TOUR3D_BANDS } from "../lib/pricingDefaults";
import { trackEvent } from "../lib/analytics";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const SERVICES_CANON = ["Casa Vacanza", "B&B / Affittacamere", "Hotel / Resort", "Agenzia Immobiliare", "Property Manager", "Altro"];
const DATE_LOCALES = { it: itLocale, en: enGB, de: deLocale };

const empty = { name: "", email: "", phone: "", service: "", location: "", preferred_date: "", message: "" };

const inputCls =
  "bg-[#0D0F12] border-white/10 text-[#F9F8F6] placeholder:text-[#666] focus-visible:ring-[#D6705A] focus-visible:border-[#D6705A]/60 h-12 rounded-none";

// Riga opzione: intera area cliccabile, min 52px di altezza touch, stato selezionato con bordo/sfondo terracotta + check
const optionCls = (active) =>
  `flex min-h-[52px] w-full items-center justify-between gap-4 border px-5 py-3.5 text-left transition-[border-color,background-color] duration-300 ${
    active ? "border-[#D6705A] bg-[#D6705A]/[0.08]" : "border-white/10 bg-white/[0.02] hover:border-white/25"
  }`;

const OptionCheck = ({ active }) => (
  <span
    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-[border-color,background-color,color] duration-300 ${
      active ? "border-[#D6705A] bg-[#D6705A] text-[#0D0F12]" : "border-white/20 text-transparent"
    }`}
  >
    <Check className="h-3 w-3" />
  </span>
);

const toDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const calendarClassNames = {
  caption_label: "text-sm font-medium text-[#F9F8F6] capitalize",
  head_cell: "text-[#666] rounded-md w-9 font-normal text-[0.75rem]",
  day: "h-9 w-9 p-0 font-normal text-[#C9C7C3] hover:bg-white/10 rounded-full transition-colors aria-selected:opacity-100",
  day_selected: "bg-[#D6705A] text-[#0D0F12] hover:bg-[#D6705A] hover:text-[#0D0F12] focus:bg-[#D6705A] focus:text-[#0D0F12]",
  day_today: "text-[#D6705A] font-semibold",
  day_outside: "day-outside text-[#555] opacity-50",
  day_disabled: "text-[#555] opacity-40 line-through hover:bg-transparent",
  nav_button: "h-7 w-7 bg-transparent p-0 text-[#A0A0A0] opacity-70 hover:opacity-100 border-white/10",
};

export const Booking = ({ origin = "site" }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = DATE_LOCALES[i18n.language] || itLocale;
  const serviceLabels = t("booking.services", { returnObjects: true });
  const perks = t("booking.perks", { returnObjects: true });

  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [pkg, setPkg] = useState("");
  const [pack, setPack] = useState("");
  const [extraKeys, setExtraKeys] = useState([]);
  const [aerialOption, setAerialOption] = useState("");
  const [surface, setSurface] = useState("");
  const [blockedDates, setBlockedDates] = useState([]);
  const [dateOpen, setDateOpen] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    api.get("/pricing")
      .then((r) => {
        const d = r.data || {};
        if (d.packages?.length) setPricing(d);
      })
      .catch(() => {});
    api.get("/availability")
      .then((r) => setBlockedDates(r.data?.blocked || []))
      .catch(() => {});
  }, []);

  // Traccia la prima visualizzazione del form (apertura form)
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((en) => en.isIntersecting)) {
          trackEvent("form_open");
          obs.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const isPremium = pkg === "premium";
  const isSignature = pkg === "signature";

  const selectPkg = (key) => {
    markStarted();
    const next = pkg === key ? "" : key;
    setPkg(next);
    if (next === "premium") {
      // Premium include già contenuti aerei e tour 3D: non acquistabili una seconda volta
      setExtraKeys((arr) => arr.filter((k) => k !== "virtual-tour" && k !== "aerei"));
      setAerialOption("");
    }
    if (next === "signature" && extraKeys.includes("aerei")) setAerialOption("video");
  };

  const toggleExtra = (k) => {
    markStarted();
    const has = extraKeys.includes(k);
    if (!has) {
      if (k === "aerei") setAerialOption(isSignature ? "video" : aerialOption || "foto");
      if (k === "virtual-tour" && !surface) setSurface("fino-80");
    }
    setExtraKeys(has ? extraKeys.filter((x) => x !== k) : [...extraKeys, k]);
  };

  const selectedPkg = pricing.packages.find((p) => p.key === pkg);
  const selectedPack = pricing.photo_packs.find((p) => p.key === pack);
  const visibleExtras = pricing.extras.filter((x) => !(isPremium && (x.key === "virtual-tour" || x.key === "aerei")));
  const specialistExtras = visibleExtras.filter((x) => x.key === "aerei" || x.key === "virtual-tour");
  const otherExtras = visibleExtras.filter((x) => x.key !== "aerei" && x.key !== "virtual-tour");
  const selectedExtras = pricing.extras.filter((x) => extraKeys.includes(x.key));

  // Prezzo effettivo di un extra in base all'opzione (contenuti aerei) o alla fascia di metratura (tour 3D)
  const extraPrice = (x) => {
    if (x.key === "aerei") {
      if (isSignature) return x.price + SIGNATURE_AERIAL_VIDEO_DELTA;
      return x.price + (AERIAL_OPTIONS.find((o) => o.key === (aerialOption || "foto"))?.delta ?? 0);
    }
    if (x.key === "virtual-tour") {
      const d = TOUR3D_BANDS.find((b) => b.key === (surface || "fino-80"))?.delta;
      return d == null ? null : x.price + d;
    }
    return x.price;
  };

  const premiumSurcharge = isPremium ? PREMIUM_SURFACE.find((s) => s.key === surface)?.surcharge ?? 0 : 0;
  const needsQuote = surface === "oltre-400" && (isPremium || extraKeys.includes("virtual-tour"));
  const hasSelection = Boolean(selectedPkg || selectedPack || selectedExtras.length);
  const estimate =
    (selectedPkg?.price || 0) +
    (selectedPack?.price || 0) +
    selectedExtras.reduce((s, x) => s + (extraPrice(x) ?? 0), 0) +
    (premiumSurcharge ?? 0);

  const extraName = (x) => t(`db.extra.${x.key}.name`, { defaultValue: x.name });
  const extraSummary = (x) => {
    if (x.key === "aerei") return `${extraName(x)} (${t(`booking.aerial.${aerialOption || "foto"}`)})`;
    if (x.key === "virtual-tour") return `${extraName(x)} (${t(`booking.surface.${surface || "fino-80"}`)})`;
    return extraName(x);
  };

  const upsell = (() => {
    if (!selectedPkg) return null;
    const perksText = t(`booking.upsellPerks.${pkg}`, { defaultValue: "" });
    if (!perksText) return null;
    const order = ["essenziale", "signature", "premium"];
    const next = pricing.packages.find((p) => p.key === order[order.indexOf(pkg) + 1]);
    if (!next) return null;
    return { target: next, diff: next.price - selectedPkg.price, perks: perksText };
  })();

  // PreventivoAvviato (evento custom Meta): SOLO dalla landing, alla prima interazione reale
  // col configuratore (scelta servizio/pacchetto/extra o compilazione di un campo). Mai all'apertura pagina.
  const startedRef = useRef(false);
  const markStarted = () => {
    if (origin !== "landing" || startedRef.current) return;
    startedRef.current = true;
    trackMetaCustom("PreventivoAvviato", { content_name: "Configuratore landing case vacanze" });
  };

  const set = (k) => (e) => {
    markStarted();
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error(t("booking.errService"));
      return;
    }
    setLoading(true);
    try {
      const lead = { value: estimate, currency: "EUR", package: pkg || undefined, extras: extraKeys, language: i18n.language };
      let utm = null;
      try { utm = JSON.parse(sessionStorage.getItem("loi-utm") || "null"); } catch { utm = null; }
      await api.post("/bookings", { ...form, package: pkg, photo_pack: pack, extras: extraKeys, surface_area: surface, aerial_option: aerialOption, lang: i18n.language, ...(utm || {}) });
      // Lead (Meta) solo a invio effettivamente riuscito: errori di validazione o di rete non lo attivano
      trackMeta("Lead", { content_name: origin === "landing" ? "Landing case vacanze" : "Sito principale", value: estimate, currency: "EUR" });
      trackEvent("generate_lead", lead);
      setDone(true);
      setForm(empty);
      setPkg("");
      setPack("");
      setExtraKeys([]);
      setAerialOption("");
      setSurface("");
      toast.success(t("booking.toastSuccess"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="prenota" ref={sectionRef} className="relative bg-[#0D0F12] px-6 py-28 lg:px-12 lg:py-40 grain" data-testid="booking-section">
      <div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-code text-[11px] uppercase tracking-[0.35em] text-[#D6705A]">{t("booking.overline")}</p>
            <h2 className="mt-4 font-display text-4xl font-light leading-tight text-[#F9F8F6] sm:text-5xl lg:text-6xl">
              {t("booking.h2pre")} <em className="italic text-[#D6705A]">{t("booking.h2em")}</em>.
            </h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-[#A0A0A0] md:text-base">
              {t("booking.intro")}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <ul className="mt-10 space-y-4">
              {perks.map((p) => (
                <li key={p} className="flex items-center gap-3 text-sm text-[#C9C7C3]">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full border border-[#D6705A]/40">
                    <Check className="h-3 w-3 text-[#D6705A]" />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          {done ? (
            <div className="flex h-full min-h-[420px] flex-col items-center justify-center border border-white/10 bg-[#16191D] p-12 text-center" data-testid="booking-success">
              <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[#D6705A]/40">
                <Check className="h-7 w-7 text-[#D6705A]" />
              </span>
              <h3 className="mt-8 font-display text-3xl font-light text-[#F9F8F6]">{t("booking.successTitle")}</h3>
              <p className="mt-3 max-w-sm text-sm text-[#A0A0A0]">
                {t("booking.successText")}
              </p>
              <button
                onClick={() => setDone(false)}
                className="mt-8 font-code text-[11px] uppercase tracking-[0.25em] text-[#D6705A] underline-offset-4 hover:underline"
                data-testid="booking-new-request"
              >
                {t("booking.newRequest")}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-[#16191D] p-8 md:p-12" data-testid="booking-form">
              <div>
                <label className="mb-3 block font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0]">
                  {t("booking.step1")}
                </label>
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger className={`${inputCls} w-full`} data-testid="booking-service-select">
                    <SelectValue placeholder={t("booking.selectPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#16191D] border-white/10 text-[#F9F8F6]">
                    {SERVICES_CANON.map((canon, i) => (
                      <SelectItem key={canon} value={canon} data-testid={`booking-service-option-${canon.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {serviceLabels[i] || canon}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-8">
                <label className="mb-3 block font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0]">
                  {t("booking.step2")}
                </label>
                <div className="grid gap-3 sm:grid-cols-3">
                  {pricing.packages.map((p) => (
                    <button
                      type="button"
                      key={p.key}
                      onClick={() => selectPkg(p.key)}
                      className={`border p-4 text-left transition-[border-color,background-color] duration-300 ${
                        pkg === p.key ? "border-[#D6705A] bg-[#D6705A]/10" : "border-white/10 hover:border-white/30"
                      }`}
                      data-testid={`config-package-${p.key}`}
                    >
                      <p className="font-display text-lg font-light text-[#F9F8F6]">{p.name}</p>
                      <p className="mt-1 font-code text-xs text-[#D6705A]">{t("pricing.from")} €{p.price}</p>
                    </button>
                  ))}
                </div>

                {upsell && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border border-[#D6705A]/30 bg-[#D6705A]/5 px-5 py-4" data-testid="upsell-banner">
                    <p className="max-w-md text-sm leading-snug text-[#C9C7C3]">
                      <span className="mb-1 block font-code text-[9px] uppercase tracking-[0.25em] text-[#D6705A]">{t("booking.upsellLabel")}</span>
                      {t("booking.upsellText", { name: upsell.target.name, perks: upsell.perks, diff: upsell.diff })}
                    </p>
                    <button
                      type="button"
                      onClick={() => selectPkg(upsell.target.key)}
                      className="flex items-center gap-2 rounded-full border border-[#D6705A]/50 px-5 py-2.5 font-code text-[10px] uppercase tracking-[0.2em] text-[#D6705A] transition-[background-color,color] duration-300 hover:bg-[#D6705A] hover:text-[#0D0F12]"
                      data-testid="upsell-upgrade-button"
                    >
                      <ArrowUp className="h-3.5 w-3.5" /> {t("booking.upsellBtn", { name: upsell.target.name })}
                    </button>
                  </div>
                )}

                {isPremium && (
                  <div className="mt-4 border border-white/10 bg-white/[0.02] p-5" data-testid="config-premium-surface">
                    <label className="mb-3 block font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0]">
                      {t("booking.surfaceLabel")}
                    </label>
                    <div className="grid gap-2.5 sm:grid-cols-2">
                      {PREMIUM_SURFACE.map((s) => (
                        <button
                          type="button"
                          key={s.key}
                          onClick={() => setSurface(surface === s.key ? "" : s.key)}
                          className={optionCls(surface === s.key)}
                          data-testid={`config-premium-surface-${s.key}`}
                        >
                          <span className="text-sm text-[#F9F8F6]">{t(`booking.surface.${s.key}`)}</span>
                          <span className="flex items-center gap-3">
                            <span className="font-code text-xs text-[#D6705A]">
                              {s.surcharge == null ? t("booking.onQuote") : s.surcharge === 0 ? t("booking.surfaceIncluded") : `+€${s.surcharge}`}
                            </span>
                            <OptionCheck active={surface === s.key} />
                          </span>
                        </button>
                      ))}
                    </div>
                    <p className="mt-3 text-[11px] leading-snug text-[#666]" data-testid="config-premium-surface-note">
                      {t("booking.surfacePremiumNote")}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-8">
                <label className="mb-3 block font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0]">
                  {t("booking.step3")}
                </label>

                {/* Servizi specialistici */}
                <p className="mb-2 font-code text-[9px] uppercase tracking-[0.25em] text-[#D6705A]" data-testid="specialist-label">
                  {t("booking.specialistLabel")}
                </p>
                <div className="grid gap-2.5 sm:grid-cols-2">
                  {specialistExtras.map((x) => (
                    <button
                      type="button"
                      key={x.key}
                      onClick={() => toggleExtra(x.key)}
                      className={optionCls(extraKeys.includes(x.key))}
                      data-testid={`config-extra-${x.key}`}
                    >
                      <span>
                        <span className="block text-sm text-[#F9F8F6]">{extraName(x)}</span>
                        <span className="mt-0.5 block font-code text-[11px] text-[#A0A0A0]">
                          {x.key === "aerei" && isSignature ? `+€${x.price + SIGNATURE_AERIAL_VIDEO_DELTA}` : `${t("pricing.from")} €${x.price}`}
                        </span>
                      </span>
                      <OptionCheck active={extraKeys.includes(x.key)} />
                    </button>
                  ))}
                </div>

                {extraKeys.includes("aerei") && (
                  <div className="mt-3 border border-white/10 bg-white/[0.02] p-4" data-testid="config-aerial-options">
                    <label className="mb-3 block font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0]">
                      {t("booking.aerialLabel")}
                    </label>
                    {isSignature && (
                      <p className="mb-3 text-[11px] leading-snug text-[#666]" data-testid="aerial-signature-note">
                        {t("booking.aerialSignatureNote")}
                      </p>
                    )}
                    <div className="grid gap-2.5">
                      {(isSignature ? AERIAL_OPTIONS.filter((o) => o.key === "video") : AERIAL_OPTIONS).map((o) => {
                        const base = pricing.extras.find((x) => x.key === "aerei")?.price ?? 150;
                        const delta = isSignature ? SIGNATURE_AERIAL_VIDEO_DELTA : o.delta;
                        return (
                          <button
                            type="button"
                            key={o.key}
                            onClick={() => setAerialOption(o.key)}
                            className={optionCls(aerialOption === o.key)}
                            data-testid={`config-aerial-${o.key}`}
                          >
                            <span className="text-sm text-[#F9F8F6]">{t(`booking.aerial.${o.key}`)}</span>
                            <span className="flex items-center gap-3">
                              <span className="font-code text-xs text-[#D6705A]">€{base + delta}</span>
                              <OptionCheck active={aerialOption === o.key} />
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {extraKeys.includes("virtual-tour") && !isPremium && (
                  <div className="mt-3 border border-white/10 bg-white/[0.02] p-4" data-testid="config-surface-select">
                    <label className="mb-3 block font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0]">
                      {t("booking.surfaceLabel")}
                    </label>
                    <div className="grid gap-2.5 sm:grid-cols-2">
                      {TOUR3D_BANDS.map((b) => {
                        const base = pricing.extras.find((x) => x.key === "virtual-tour")?.price ?? 220;
                        return (
                          <button
                            type="button"
                            key={b.key}
                            onClick={() => setSurface(b.key)}
                            className={optionCls(surface === b.key)}
                            data-testid={`config-surface-${b.key}`}
                          >
                            <span className="text-sm text-[#F9F8F6]">{t(`booking.surface.${b.key}`)}</span>
                            <span className="flex items-center gap-3">
                              <span className="font-code text-xs text-[#D6705A]">
                                {b.delta == null ? t("booking.onQuote") : `€${base + b.delta}`}
                              </span>
                              <OptionCheck active={surface === b.key} />
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Altri servizi */}
                <p className="mb-2 mt-6 font-code text-[9px] uppercase tracking-[0.25em] text-[#A0A0A0]" data-testid="other-label">
                  {t("booking.otherLabel")}
                </p>
                <div className="grid gap-2.5 sm:grid-cols-2">
                  {pricing.photo_packs.map((p) => (
                    <button
                      type="button"
                      key={p.key}
                      onClick={() => {
                        markStarted();
                        setPack(pack === p.key ? "" : p.key);
                      }}
                      className={optionCls(pack === p.key)}
                      data-testid={`config-pack-${p.key}`}
                    >
                      <span className="text-sm text-[#F9F8F6]">+{p.qty} {t("booking.photosUnit")}</span>
                      <span className="flex items-center gap-3">
                        <span className="font-code text-xs text-[#D6705A]">€{p.price}</span>
                        <OptionCheck active={pack === p.key} />
                      </span>
                    </button>
                  ))}
                  {otherExtras.map((x) => (
                    <button
                      type="button"
                      key={x.key}
                      onClick={() => toggleExtra(x.key)}
                      className={optionCls(extraKeys.includes(x.key))}
                      data-testid={`config-extra-${x.key}`}
                    >
                      <span className="text-sm text-[#F9F8F6]">{extraName(x)}</span>
                      <span className="flex items-center gap-3">
                        <span className="font-code text-xs text-[#D6705A]">€{x.price}</span>
                        <OptionCheck active={extraKeys.includes(x.key)} />
                      </span>
                    </button>
                  ))}
                </div>
                {extraKeys.includes("stagionale") && (
                  <p className="mt-3 text-[11px] leading-snug text-[#666]" data-testid="seasonal-note">
                    {t("booking.seasonalNote")}
                  </p>
                )}
              </div>

              {hasSelection && (
                <div className="mt-8 flex items-center justify-between border border-[#D6705A]/40 bg-[#D6705A]/5 px-6 py-5" data-testid="config-estimate">
                  <div>
                    <p className="font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0]">{t("booking.estimate")}</p>
                    <p className="mt-1 text-xs text-[#666]">
                      {[selectedPkg?.name, selectedPack ? `+${selectedPack.qty} ${t("booking.photosUnit")}` : null, ...selectedExtras.map(extraSummary)]
                        .filter(Boolean)
                        .join(" + ")}
                    </p>
                    {needsQuote && (
                      <p className="mt-2 text-[11px] leading-snug text-[#D6705A]" data-testid="config-estimate-quote-note">
                        {t("booking.quoteNote")}
                      </p>
                    )}
                  </div>
                  <p className="font-display text-4xl font-light text-[#D6705A]" data-testid="config-estimate-price">
                    €{estimate} <span className="font-code text-[10px] uppercase tracking-[0.2em] text-[#666]">{t("booking.vat")}</span>
                  </p>
                </div>
              )}
              {hasSelection && (
                <p className="mt-3 text-[11px] leading-snug text-[#666]" data-testid="config-estimate-travel-note">
                  {t("booking.locationNote")}
                </p>
              )}

              <div className="mt-10 border-t border-white/10 pt-8">
                <label className="mb-4 block font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0]">
                  {t("booking.step4")}
                </label>
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0]">{t("booking.location")} *</label>
                    <Input required value={form.location} onChange={set("location")} placeholder={t("booking.locationPlaceholder")} className={inputCls} data-testid="booking-location-input" />
                    <p className="mt-2 text-[11px] leading-snug text-[#666]" data-testid="booking-travel-note">
                      {t("booking.locationNote")}
                    </p>
                  </div>
                  <div>
                    <label className="mb-2 block font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0]">{t("booking.date")}</label>
                    <Popover open={dateOpen} onOpenChange={setDateOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className={`${inputCls} flex w-full items-center justify-between px-3 text-left text-sm font-normal ${form.preferred_date ? "" : "text-[#666]"}`}
                          data-testid="booking-date-trigger"
                        >
                          {form.preferred_date
                            ? format(toDate(form.preferred_date), "d MMMM yyyy", { locale: dateLocale })
                            : t("booking.datePlaceholder")}
                          <CalendarIcon className="h-4 w-4 shrink-0 text-[#666]" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-auto border-white/10 bg-[#16191D] p-0 text-[#F9F8F6]">
                        <Calendar
                          mode="single"
                          locale={dateLocale}
                          selected={form.preferred_date ? toDate(form.preferred_date) : undefined}
                          onSelect={(d) => {
                            if (!d) return;
                            setForm((f) => ({ ...f, preferred_date: format(d, "yyyy-MM-dd") }));
                            setDateOpen(false);
                          }}
                          disabled={[{ before: new Date() }, ...blockedDates.map(toDate)]}
                          classNames={calendarClassNames}
                          initialFocus
                          data-testid="booking-calendar"
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="mt-2 text-[11px] leading-snug text-[#666]">
                      {t("booking.dateNote")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <label className="mb-4 block font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0]">
                  {t("booking.step5")}
                </label>
                <div className="grid gap-6 md:grid-cols-3">
                  <div>
                    <label className="mb-2 block font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0]">{t("booking.name")} *</label>
                    <Input required value={form.name} onChange={set("name")} placeholder="Maria Rossi" className={inputCls} data-testid="booking-name-input" />
                  </div>
                  <div>
                    <label className="mb-2 block font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0]">{t("booking.phone")} *</label>
                    <Input required value={form.phone} onChange={set("phone")} placeholder="+39 333 000 0000" className={inputCls} data-testid="booking-phone-input" />
                  </div>
                  <div>
                    <label className="mb-2 block font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0]">Email *</label>
                    <Input required type="email" value={form.email} onChange={set("email")} placeholder="maria@esempio.it" className={inputCls} data-testid="booking-email-input" />
                  </div>
                </div>
                <div className="mt-6">
                  <label className="mb-2 block font-code text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0]">{t("booking.message")} *</label>
                  <Textarea
                    required
                    value={form.message}
                    onChange={set("message")}
                    placeholder={t("booking.messagePlaceholder")}
                    className={`${inputCls} min-h-[120px] resize-none`}
                    data-testid="booking-message-input"
                  />
                  <p className="mt-2 text-[11px] leading-snug text-[#666]">
                    {t("booking.messageNote")}
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-[#D6705A] px-8 py-4 font-code text-[11px] uppercase tracking-[0.25em] text-[#0D0F12] transition-[background-color,transform] duration-300 hover:bg-[#F9F8F6] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 md:w-auto"
                data-testid="booking-submit-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {loading ? t("booking.sending") : t("booking.submit")}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
